import React, { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import Map, { Marker } from 'react-map-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import { useFetchData } from 'services/api/useFetchData';
import { Avatar } from 'components/layout/Avatar';
import { isEmployeeStale } from './MapMarkers';
import { MapEmployeeType } from '../SchedulingTypes';
import { mapboxPlaceholderAccessToken, transformMapboxRequest } from './mapboxProxy';

interface MapPreviewCardProps {
    onClick: () => void;
}

// Default map center on the Netherlands, used until the company address is known
const DEFAULT_VIEW_STATE = { longitude: 5.2913, latitude: 52.1326, zoom: 6.5 };

// Interval in milliseconds between employee position refreshes on the preview
const PREVIEW_EMPLOYEE_REFRESH_MS = 30000;

const MapPreviewCard: React.FC<MapPreviewCardProps> = ({ onClick }) => {
    const { t } = useTranslation();

    // Fetch the company address coordinates to center the preview on
    const { response: mapCenter } = useFetchData({ apiUrl: 'get_scheduler_map_center', useCache: true });

    // Fetch the current employee positions to show on the preview
    const { response: employeesResponse, refetch: refetchEmployees } = useFetchData({ apiUrl: 'get_scheduler_map_employees' });
    const employees: MapEmployeeType[] = employeesResponse?.employees ?? [];

    // Keep the latest refetch function in a ref, its identity changes on every render
    const refetchEmployeesRef = useRef(refetchEmployees);
    refetchEmployeesRef.current = refetchEmployees;

    // Refresh the employee positions on a fixed interval, the ref keeps the timer
    // stable while the parent rerenders on the scheduler event polling
    useEffect(() => {
        const interval = setInterval(() => refetchEmployeesRef.current(), PREVIEW_EMPLOYEE_REFRESH_MS);
        return () => clearInterval(interval);
    }, []);

    // Center the preview on the company address when known, zoomed in a bit further
    const viewState = mapCenter?.latitude && mapCenter?.longitude
        ? { longitude: parseFloat(mapCenter.longitude), latitude: parseFloat(mapCenter.latitude), zoom: 11 }
        : DEFAULT_VIEW_STATE;

    return (
        <div
            className="scheduler-map-preview-card"
            onClick={onClick}
            title={t('scheduling.scheduler_map.preview_card_tooltip')}
        >
            {/* Interaction is blocked via css pointer events, so this map can share reused
                instances with the full map without differing constructor options */}
            <Map
                {...viewState}
                reuseMaps
                mapboxAccessToken={mapboxPlaceholderAccessToken}
                transformRequest={transformMapboxRequest}
                mapStyle="mapbox://styles/mapbox/streets-v12"
                style={{ width: '100%', height: '100%' }}
            >
                {/* Show the current employee positions with their avatar and status ring */}
                {employees
                    .filter(employee => employee.latitude && employee.longitude)
                    .map(employee => (
                        <Marker
                            key={`preview-employee-${employee.id}`}
                            longitude={parseFloat(employee.longitude!)}
                            latitude={parseFloat(employee.latitude!)}
                            anchor="center"
                        >
                            <div className={`scheduler-map-employee-pin preview ${employee.status} ${isEmployeeStale(employee.last_gps_at) ? 'stale' : ''}`}>
                                {employee.image ? (
                                    <div className="scheduler-map-employee-avatar-image" style={{ backgroundImage: `url(${employee.image})` }} />
                                ) : (
                                    <Avatar name={employee.name} size="1.5rem" />
                                )}
                            </div>
                        </Marker>
                    ))}
            </Map>
        </div>
    );
};

export default MapPreviewCard;
